import React, {useContext, useMemo} from 'react';


import {Skeleton} from 'src/components/skeleton';
import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {Group, Item} from '@pb/kit-component-group';
import SlideBox from '@pb/kit-component-slide-box';
import StateButton from '@pb/kit-component-state-button';
import {Cell, Row} from '@pb/kit-component-table';

import {CrmRowSkeleton} from './crm-row-skeleton';

import type {CrmType} from 'src/openapi/admin/models/CrmType';
import type {Error} from 'src/openapi/admin/models/Error';
import type {GetCrmLimit} from 'src/openapi/admin/models/GetCrmLimit';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    currentCrm: CrmType | null,
    crmType: CrmType,
    crmName: string,

    editDisabled?: boolean,
    enabled: boolean,
    onToggleCrmModules: React.MouseEventHandler<HTMLDivElement>,

    crmLimits?: GetCrmLimit,

    children: React.ReactNode,
    connectionStatus: React.ReactNode,
    slideBoxButton: React.ReactNode,
};
export const CrmRow = ({
    isLoading, children, crmType, currentCrm, crmName,
    editDisabled, enabled, onToggleCrmModules,
    crmLimits, connectionStatus, slideBoxButton, loadingError,
}: Props) => {
    const {changedChunks} = useContext(ConfirmationContext);

    const loadingErrorView = useMemo(() => {
        if (loadingError && loadingError.status === 403) {
            return <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to this page"/>;
        } else if (loadingError) {
            return <ErrorPage title="Service is temporarily unavailable" subtitle="Please try later"/>;
        } else {
            return false;
        }
    }, [loadingError]);

    const moduleSwitchDisabled = editDisabled ||
        changedChunks.some(chunk => chunk.includes('crm')) &&
        currentCrm !== crmType;

    return (
        <Skeleton shown={isLoading} view={<CrmRowSkeleton/>}>
            <Row>
                <Cell>
                    <Item className="pt-s">
                        <div onClick={!moduleSwitchDisabled ? onToggleCrmModules : undefined}>
                            <StateButton
                                name={crmType}
                                disabled={moduleSwitchDisabled}
                                checked={enabled}
                            />
                        </div>
                    </Item>
                </Cell>
                <Cell>
                    <Group justify="space-between" alignItems="center">
                        <Group>
                            <Item style={{width: 80}}>{crmName}</Item>
                            <Item style={{width: 100}}>
                                <Group justify="end" gap="xxs">
                                    <Item>{crmLimits?.currentUsers ?? '--'}</Item>
                                    /
                                    <Item>{crmLimits?.maxUsers ?? '--'}</Item>
                                </Group>
                            </Item>
                            <Item style={{width: 150}}>
                                <Group justify="end" gap="xxs">
                                    <Item>{crmLimits?.currentImports ?? '--'}</Item>
                                    /
                                    <Item>{crmLimits?.maxImports ?? '--'}</Item>
                                </Group>
                            </Item>
                            <Item style={{width: 150}}>
                                <Group justify="end" gap="xxs">
                                    <Item>{crmLimits?.currentImportCredits ?? '--'}</Item>
                                    /
                                    <Item>{crmLimits?.importCredits ?? '--'}</Item>
                                </Group>
                            </Item>
                            {connectionStatus}
                        </Group>
                        {slideBoxButton}
                    </Group>
                    <SlideBox shown={currentCrm === crmType}>
                        <Item className="pt-xl pb-m">
                            {loadingError ? loadingErrorView : children}
                        </Item>
                    </SlideBox>
                </Cell>
            </Row>
        </Skeleton>
    );
};
